import { useEffect, useState, useRef } from "react";
import './IncomingCall.css';
import { useAuth } from "../../Context/Auth";
import CallEndIcon from '@mui/icons-material/CallEnd';
import CallIcon from '@mui/icons-material/Call';
import { acceptCall, preAcceptCall, rejectCall, terminateCall, sendIceCandidate, confirmConnected } from "../../API/CommonApi";


export default function IncomingCall({ call }) {
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [answerSdp, setAnswerSdp] = useState(null);
  const [callDuration, setCallDuration] = useState(0); // in seconds
  const [incomingCall, setIncomingCall] = useState(null);
  const [callHidden, setCallHidden] = useState(false);
  const pcRef = useRef(null);
  const auth = useAuth();
  const user = auth?.user?.user;


  // Use your WebRTC library to generate an SDP answer
  async function createAnswer(offerSdp) {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" } // Google's public STUN server
      ]
    });
    pcRef.current = pc;
    pc.ontrack = (e) => {
      const audio = new Audio();
      audio.srcObject = e.streams[0];
      audio.autoplay = true;
      document.body.appendChild(audio);

      confirmConnected(call.call_id);
    };
    await pc.setRemoteDescription({ type: "offer", sdp: offerSdp });

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => pc.addTrack(track, stream));


    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    // Wait for ICE gathering to complete
    await new Promise(resolve => {
      if (pc.iceGatheringState === "complete") return resolve();
      const checkState = () => {
        if (pc.iceGatheringState === "complete") {
          pc.removeEventListener("icegatheringstatechange", checkState);
          resolve();
        }
      };
      pc.addEventListener("icegatheringstatechange", checkState);
    });
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        
        try {
          sendIceCandidate(call.call_id, event.candidate);
        } catch (err) {
          console.error("ICE candidate error:", err);
        }
      }
    };
    pc.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "ice.candidate") {
        pc.addIceCandidate(new RTCIceCandidate(data.candidate));
      }
    };


    return pc.localDescription.sdp;
  }



  useEffect(() => {
    if (!call) return;
    
    if (call.status === "connect" || call.status === "accept") {
      setIncomingCall({
        callId: call.call_id,
        from: call?.caller || call?.from,
        timestamp: call.timestamp,
        company: call?.company || auth?.wpDetails[0]?.company,
      });
      setCallHidden(false);
    }

    if (call.type === "call.accepted" && call.status === "accept") {
      if (incomingCall && call.call_id === incomingCall.callId) {
        const acceptedBy = call.accepted_by;
        if (acceptedBy && acceptedBy !== user?.pk) {
          setCallHidden(true);
        } else {
          setCallHidden(false); // keep UI visible if you accepted
        }
      }
    }
  }, [call]);


  useEffect(() => {
    let timer;
    if (call.status === "accept" && !accepting) {
      timer = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    
    if (call.status === "terminate" || call.status === "reject") {
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      setCallDuration(0);
      document.querySelectorAll("audio").forEach(audio => audio.remove());
    }
    return () => clearInterval(timer); // cleanup on unmount or status change
  }, [call.status, accepting]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };


  async function handleAccept() {
    setAccepting(true);
    const sdp = await createAnswer(call.session.sdp);
    setAnswerSdp(sdp);
    // console.log("Prepared SDP answer:", sdp);
    
    try {
      await preAcceptCall(call.call_id, sdp);
    } catch (err) {
      console.error("pre-accept call error:", err);
    }
    
    try {
      await acceptCall(call.call_id, sdp);
    } catch (err) {
      console.error("Call accept error:", err);
    }
    setAccepting(false);
  }


  async function handleReject() {
    setRejecting(true);
    
    try {
      await rejectCall(call.call_id);
    } catch (err) {
      console.error("Call reject error:", err);
    }
    setRejecting(false);
    setCallHidden(true);
  }

  async function handleTerminate() {
    setTerminating(true);
    
    try {
      await terminateCall(call.call_id);
    } catch (err) {
      console.error("Call terminate error:", err);
    }
    setTerminating(false);
    setCallHidden(true);
  }

  return (
    <div className="call-popup-overlay">
      { incomingCall !== null && !callHidden && call.direction === "USER_INITIATED" && (
      <div className="call-popup-container">
        <h3 className="caller-name">{call.from}</h3>
        <h5 className="caller-number">{call.number}</h5>
        {call.status === "accept" && !accepting && (
          <p className="call-timer">Duration: {formatDuration(callDuration)}</p>
        )}
        <p className="call-status">
          {call?.status === "connect"  && <p>Ringing…</p>}
          {call?.status === "accept" && accepting && <p>Connecting...</p>}
          {rejecting    && <p>Call rejecting....</p>}
          {terminating    && <p>Call declining....</p>}
        </p>
        <div className="call-actions">
            {call.status === "connect" && (
          <>
            <button className="accept-btn" onClick={handleAccept}>
              {accepting ? <CallIcon disabled /> : <CallIcon />}
            </button>
            <button className="reject-btn" onClick={handleReject}>
              {rejecting ? <CallEndIcon disabled /> : <CallEndIcon />}
            </button>
          </>
        )}

        {call.status === "accept" && (
          <button className="reject-btn" onClick={handleTerminate}>
            {terminating ? <CallEndIcon disabled /> : <CallEndIcon />}
          </button>
        )}
        </div>
      </div>
      )}
    </div>
  );
}

